import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  SubdirectoryArrowRight as ArrowIcon,
  FlagOutlined as FlagIcon,
  MoveDownOutlined as MoveIcon,
} from "@mui/icons-material";
import Etiqueta from "../../components/kit/Etiqueta";
import { useCandidatosAMover, useGuardarFormulario, useMoverEstudiantes, usePasos, useUpdatePaso } from "../../queries/usePasos";
import { usePeriodos } from "../../queries/useEvaluadores";
import {
  Paso,
  OpcionPregunta,
  PreguntaEvaluacion,
  ReglaResultado,
  REGLAS_RESULTADO,
  REGLAS_INGRESO,
  ReglaIngreso,
  ResultadoPoblado,
  TipoPregunta,
} from "../../types/paso";
import { mensajeDeError } from "../../utils/errores";
import { ordenarPasosPorFlujo } from "../../utils/flujoPasos";
import { SEMESTRES } from "../../utils/semestre";

const TIPOS: { value: TipoPregunta; label: string }[] = [
  { value: "escala", label: "Número (nota de 1 a 7)" },
  { value: "opcion", label: "Opción única" },
  { value: "texto", label: "Texto libre" },
];

/** "Pasos" (coordinador): tamaño de comisión y formulario de evaluación por paso. */
export default function Pasos() {
  const { data, isLoading, isError } = usePasos();
  const [editar, setEditar] = useState<Paso | null>(null);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Pasos y evaluación
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define los parámetros de la comisión de cada paso (evaluadores, si se incluye al guía/coguía, optimización por áreas) y
        arma su formulario de evaluación.
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudieron cargar los pasos.</Alert>}

      <Stack spacing={2}>
        {ordenarPasosPorFlujo(data ?? []).map((paso) => (
          <PasoCard key={paso.id} paso={paso} pasos={data ?? []} onEditarForm={() => setEditar(paso)} />
        ))}
      </Stack>

      {editar && <FormularioBuilder paso={editar} onClose={() => setEditar(null)} />}
    </Box>
  );
}

/** Texto de la pregunta que define el resultado del paso, o "" si no hay ninguna. */
function resultadoDe(paso: Paso) {
  const id = paso.formulario.pregunta_resultado;
  return paso.formulario.preguntas.find((q) => q.id === id)?.texto ?? "";
}

function PasoCard({ paso, pasos, onEditarForm }: { paso: Paso; pasos: Paso[]; onEditarForm: () => void }) {
  const actualizar = useUpdatePaso();
  const [tamano, setTamano] = useState(paso.tamano_comision);
  const [mover, setMover] = useState(false);

  useEffect(() => setTamano(paso.tamano_comision), [paso.tamano_comision]);

  const set = (input: Parameters<typeof actualizar.mutate>[0]["input"]) => actualizar.mutate({ id: paso.id, input });

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Typography variant="h6">{paso.nombre}</Typography>

      {/* A. Comisión evaluadora: tamaño y parámetros de optimización. */}
      <Seccion titulo="Comisión evaluadora" ayuda="Quiénes evalúan y cómo se optimiza la asignación.">
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            type="number"
            size="small"
            label="Evaluadores"
            value={tamano}
            onChange={(e) => setTamano(Math.max(0, Number(e.target.value)))}
            onBlur={() => tamano !== paso.tamano_comision && set({ tamano_comision: tamano })}
            sx={{ width: 130 }}
            inputProps={{ min: 0 }}
          />
          <FormControlLabel
            control={<Switch checked={paso.incluir_guia} disabled={actualizar.isPending} onChange={(e) => set({ incluir_guia: e.target.checked })} />}
            label="Incluir guía"
          />
          <FormControlLabel
            control={<Switch checked={paso.incluir_coguia} disabled={actualizar.isPending} onChange={(e) => set({ incluir_coguia: e.target.checked })} />}
            label="Incluir coguía"
          />
          <FormControlLabel
            control={<Switch checked={paso.optimizar_areas} disabled={actualizar.isPending} onChange={(e) => set({ optimizar_areas: e.target.checked })} />}
            label="Optimizar áreas"
          />
        </Stack>
      </Seccion>

      {/* B. Evaluación: el formulario es la evaluación y de él sale el resultado. */}
      <Seccion titulo="Evaluación" ayuda="Las preguntas que responde la comisión y cuál de ellas define el resultado.">
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Etiqueta variante="contorno">
            {paso.formulario.preguntas.length} {paso.formulario.preguntas.length === 1 ? "pregunta" : "preguntas"}
          </Etiqueta>
          {resultadoDe(paso) ? (
            <Etiqueta variante="marca">Resultado: {resultadoDe(paso)} · {paso.formulario.regla_display}</Etiqueta>
          ) : (
            <Etiqueta variante="acento">Sin pregunta de resultado</Etiqueta>
          )}
          <Button variant="outlined" onClick={onEditarForm} sx={{ ml: { sm: "auto" } }}>
            Editar formulario
          </Button>
        </Stack>
        {!resultadoDe(paso) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
            Mientras no elijas qué pregunta define el resultado, el coordinador no puede publicar los procesos de este paso.
          </Typography>
        )}
      </Seccion>

      {/* C. Ingreso de estudiantes: regla de poblado + acción de poblar. */}
      <Seccion titulo="Ingreso de estudiantes" ayuda="Cómo entran los estudiantes a este paso.">
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Regla de ingreso"
            value={paso.regla_ingreso}
            onChange={(e) => set({ regla_ingreso: e.target.value as ReglaIngreso })}
            disabled={actualizar.isPending}
            sx={{ minWidth: 230 }}
          >
            {REGLAS_INGRESO.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          {paso.regla_ingreso === "inscripcion_curso" &&
            (paso.cursos.length ? (
              paso.cursos.map((c) => (
                <Etiqueta key={c.id} variante="contorno">
                  {c.codigo}
                </Etiqueta>
              ))
            ) : (
              <Typography variant="caption" color="warning.main">
                Sin cursos configurados
              </Typography>
            ))}
          <Stack direction="row" spacing={1} sx={{ ml: { sm: "auto" } }} flexWrap="wrap" useFlexGap>
            {/* Traer a los estudiantes que le tocan al paso se hace desde la vista de
                Estudiantes, que es donde se ve la lista que eso actualiza. Aquí queda
                lo que solo tiene sentido con el catálogo a la vista: mover gente entre
                pasos, que es como se llenan los manuales (Vía Rápida). */}
            <Button size="small" variant="outlined" startIcon={<MoveIcon />} onClick={() => setMover(true)}>
              Mover estudiantes aquí…
            </Button>
          </Stack>
        </Stack>
      </Seccion>

      {/* Flujo (informativo): prerrequisitos y a qué paso(s) conduce. */}
      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={0.75}>
        <FlujoLinea
          icono={<ArrowIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />}
          etiqueta="Requiere"
          items={paso.depende_de.map((p) => p.nombre)}
          vacio="Inicio del proceso"
        />
        <FlujoLinea
          icono={paso.conduce_a.length ? <ArrowIcon fontSize="small" /> : <FlagIcon fontSize="small" />}
          etiqueta="Conduce a"
          items={paso.conduce_a.map((p) => p.nombre)}
          vacio="Fin del proceso"
        />
      </Stack>

      {mover && <MoverDialog paso={paso} pasos={pasos} onClose={() => setMover(false)} />}
    </Paper>
  );
}

/** Bloque con encabezado para agrupar una función de la tarjeta de paso. */
function Seccion({ titulo, ayuda, children }: { titulo: string; ayuda: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.4 }}>
        {titulo}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 1 }}>
        {ayuda}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Mueve procesos ya existentes a este paso. Es la contraparte manual del poblado:
 * los pasos de regla `manual` (Vía Rápida) no se llenan solos, los llena el
 * coordinador eligiendo de qué paso y cohorte traer a la gente.
 *
 * Desde un prerrequisito (Propuesta Inicial → Vía Rápida) se **continúa**: se crea el
 * proceso de este paso a continuación del de origen, que sigue abierto. Desde cualquier
 * otro paso se **mueve**: deja de estar en el de origen (sirve para corregir).
 */
function MoverDialog({ paso, pasos, onClose }: { paso: Paso; pasos: Paso[]; onClose: () => void }) {
  const { data: periodos } = usePeriodos();
  const ultimo = periodos?.[0];
  const [origen, setOrigen] = useState<number | "">("");
  const [ano, setAno] = useState<number>(ultimo?.ano ?? new Date().getFullYear());
  const [semestre, setSemestre] = useState<number>(ultimo?.semestre ?? 1);
  const [elegidos, setElegidos] = useState<number[]>([]);
  const [preview, setPreview] = useState<ResultadoPoblado | null>(null);

  const { data: candidatos = [], isFetching } = useCandidatosAMover(origen, ano, semestre, true);
  const mover = useMoverEstudiantes();

  // Cambiar de origen o cohorte invalida la selección y el preview anterior.
  const cambiar = (fn: () => void) => {
    fn();
    setElegidos([]);
    setPreview(null);
  };

  const alternar = (id: number) => {
    setElegidos((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setPreview(null);
  };

  const correr = (commit: boolean) =>
    mover.mutate({ paso_destino: paso.id, instancias: elegidos, commit }, { onSuccess: setPreview });

  const publicado = preview?.publicado;
  const continua = origen !== "" && paso.depende_de.some((p) => p.id === origen);
  const otros = pasos.filter((p) => p.id !== paso.id);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mover estudiantes a «{paso.nombre}»</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {continua
              ? `Trae procesos desde otro paso de la misma cohorte. Se crea su «${paso.nombre}» a continuación, con el mismo tema y guía, y el de origen sigue abierto hasta que se cierre: el estudiante puede entregar en cualquiera de los dos.`
              : "Trae procesos desde otro paso de la misma cohorte. El proceso se mueve: deja de estar en el paso de origen y conserva su tema, guía y lo que ya tuviera."}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              select
              size="small"
              label="Traer desde"
              value={origen}
              onChange={(e) => cambiar(() => setOrigen(Number(e.target.value)))}
              sx={{ minWidth: 200 }}
            >
              {otros.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nombre}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              size="small"
              label="Año"
              value={ano}
              onChange={(e) => cambiar(() => setAno(Number(e.target.value)))}
              sx={{ width: 120 }}
            />
            <TextField
              select
              size="small"
              label="Semestre"
              value={semestre}
              onChange={(e) => cambiar(() => setSemestre(Number(e.target.value)))}
              sx={{ minWidth: 150 }}
            >
              {SEMESTRES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {mover.isError && <Alert severity="error">{mensajeDeError(mover.error, "No se pudo mover a los estudiantes.")}</Alert>}

          {origen !== "" && !isFetching && candidatos.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No hay procesos en ese paso para esa cohorte.
            </Typography>
          )}

          {!publicado && candidatos.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Elige a quiénes mover ({elegidos.length} de {candidatos.length}):
              </Typography>
              <Stack sx={{ mt: 0.5, maxHeight: 260, overflowY: "auto" }} divider={<Divider flexItem />}>
                {candidatos.map((c) => (
                  <FormControlLabel
                    key={c.instancia_id}
                    control={<Checkbox size="small" checked={elegidos.includes(c.instancia_id)} onChange={() => alternar(c.instancia_id)} />}
                    label={
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2">{c.nombre}</Typography>
                        <Etiqueta variante="contorno">{c.estado}</Etiqueta>
                        {c.arrastra.length > 0 && <Etiqueta>lleva {c.arrastra.join(", ")}</Etiqueta>}
                      </Stack>
                    }
                    sx={{ py: 0.25, mr: 0 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {preview?.avisos.map((a, i) => (
            <Alert key={i} severity="warning">
              {a}
            </Alert>
          ))}

          {preview &&
            (publicado ? (
              <Alert severity="success">
                Se {preview.creadas.length === 1 ? "movió 1 proceso" : `movieron ${preview.creadas.length} procesos`} a {paso.nombre}.
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {preview.creadas.length === 0 ? "No hay nada que mover con esa selección." : `Se moverán ${preview.creadas.length} proceso(s).`}
              </Typography>
            ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={onClose}>{publicado ? "Cerrar" : "Cancelar"}</Button>
        {!publicado && (
          <Stack direction="row" spacing={1}>
            <Button onClick={() => correr(false)} disabled={mover.isPending || elegidos.length === 0}>
              {mover.isPending ? "Calculando…" : "Previsualizar"}
            </Button>
            <Button variant="contained" onClick={() => correr(true)} disabled={mover.isPending || !preview || preview.creadas.length === 0}>
              Confirmar
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}

function FlujoLinea({ icono, etiqueta, items, vacio }: { icono: React.ReactNode; etiqueta: string; items: string[]; vacio: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Box sx={{ color: "text.secondary", display: "flex" }}>{icono}</Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, minWidth: 72 }}>
        {etiqueta}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          {vacio}
        </Typography>
      ) : (
        items.map((n) => <Etiqueta key={n}>{n}</Etiqueta>)
      )}
    </Stack>
  );
}

/** Las opciones de una pregunta de opción única, cada una con el valor que aporta.
 *
 *  El valor es lo que convierte una elección en nota cuando esa pregunta es la de
 *  resultado: "Aprobado" = 7, "Reprobado" = 1. Una opción sin valor simplemente no
 *  calcula, y sirve para preguntas que no deciden nada. */
function OpcionesPregunta({ opciones, onChange }: { opciones: OpcionPregunta[]; onChange: (o: OpcionPregunta[]) => void }) {
  const set = (i: number, patch: Partial<OpcionPregunta>) => onChange(opciones.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));

  return (
    <Stack spacing={1}>
      {opciones.map((o, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <TextField size="small" label={`Opción ${i + 1}`} value={o.texto} onChange={(e) => set(i, { texto: e.target.value })} fullWidth />
          <TextField
            size="small"
            type="number"
            label="Vale"
            value={o.valor ?? ""}
            onChange={(e) => set(i, { valor: e.target.value === "" ? null : Number(e.target.value) })}
            inputProps={{ min: 1, max: 7, step: 0.1 }}
            sx={{ width: 110 }}
          />
          <IconButton color="error" size="small" onClick={() => onChange(opciones.filter((_, idx) => idx !== i))}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...opciones, { texto: "", valor: null }])} sx={{ alignSelf: "flex-start" }}>
        Agregar opción
      </Button>
    </Stack>
  );
}

function FormularioBuilder({ paso, onClose }: { paso: Paso; onClose: () => void }) {
  const guardar = useGuardarFormulario();
  const [instrucciones, setInstrucciones] = useState(paso.formulario.instrucciones);
  const [preguntas, setPreguntas] = useState<PreguntaEvaluacion[]>(paso.formulario.preguntas);
  const [resultado, setResultado] = useState<number | null>(paso.formulario.pregunta_resultado);
  const [regla, setRegla] = useState<ReglaResultado>(paso.formulario.regla);

  const set = (i: number, patch: Partial<PreguntaEvaluacion>) =>
    setPreguntas((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const agregar = () => setPreguntas((prev) => [...prev, { texto: "", tipo: "texto", opciones: [] }]);
  const quitar = (i: number) =>
    setPreguntas((prev) => {
      // Si se va la que definía el resultado, el paso queda sin resultado hasta elegir otra.
      if (prev[i]?.id === resultado) setResultado(null);
      return prev.filter((_, idx) => idx !== i);
    });

  // Solo una pregunta que aporte un número puede definir el resultado: una de texto
  // no se puede convertir en nota.
  const candidatas = preguntas.filter((p) => p.id && p.texto.trim() && p.tipo !== "texto");

  const enviar = () =>
    guardar.mutate(
      { id: paso.id, formulario: { instrucciones, preguntas, pregunta_resultado: resultado, regla } },
      { onSuccess: onClose },
    );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Formulario de evaluación — {paso.nombre}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {guardar.isError && <Alert severity="error">No se pudo guardar el formulario.</Alert>}
          <Typography variant="body2" color="text.secondary">
            Estas preguntas <strong>son</strong> la evaluación: el estudiante ve todo lo que responde cada profesor. Una de ellas
            define el resultado del proceso, y la regla dice cómo se combinan las respuestas de la comisión en una nota.
          </Typography>
          <TextField
            label="Instrucciones (opcional)"
            value={instrucciones}
            onChange={(e) => setInstrucciones(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          {preguntas.map((p, i) => (
            <Paper key={i} elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <TextField
                    label={`Pregunta ${i + 1}`}
                    value={p.texto}
                    onChange={(e) => set(i, { texto: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  <IconButton color="error" onClick={() => quitar(i)} sx={{ mt: 0.5 }}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                  <TextField select size="small" label="Tipo" value={p.tipo} onChange={(e) => set(i, { tipo: e.target.value as TipoPregunta })} sx={{ minWidth: 180 }}>
                    {TIPOS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                {p.tipo === "opcion" && <OpcionesPregunta opciones={p.opciones} onChange={(opciones) => set(i, { opciones })} />}
              </Stack>
            </Paper>
          ))}

          <Button startIcon={<AddIcon />} onClick={agregar} sx={{ alignSelf: "flex-start" }}>
            Agregar pregunta
          </Button>

          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Cómo se calcula el resultado
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                select
                size="small"
                label="Pregunta que lo define"
                value={resultado ?? ""}
                onChange={(e) => setResultado(e.target.value === "" ? null : Number(e.target.value))}
                sx={{ minWidth: 260 }}
                helperText={candidatas.length === 0 ? "Guarda primero una pregunta de nota o de opción." : " "}
              >
                <MenuItem value="">
                  <em>Ninguna</em>
                </MenuItem>
                {candidatas.map((q) => (
                  <MenuItem key={q.id} value={q.id}>
                    {q.texto}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Regla"
                value={regla}
                onChange={(e) => setRegla(e.target.value as ReglaResultado)}
                sx={{ minWidth: 240 }}
                helperText={REGLAS_RESULTADO.find((r) => r.value === regla)?.ayuda ?? " "}
              >
                {REGLAS_RESULTADO.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardar.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={enviar} disabled={guardar.isPending}>
          {guardar.isPending ? "Guardando…" : "Guardar formulario"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
